import useTranslation from 'next-translate/useTranslation';
import { match } from 'ts-pattern';
import { format } from 'date-fns';
import { Box, Skeleton } from '@mantine/core';

import { CurrencyConvertor } from '~/components/utils/formatters';
import { Select } from '~/domains/design-system/TextInput';
import { Title } from '~/domains/design-system/Title';
import { Text } from '~/domains/design-system/Text';
import { Dropdown } from '~/domains/design-system/components/Dropdown/Dropdown';

import type { LoyaltyRewardHistoryRecord } from '../../../../types/gamification.types';
import { useStyles } from './WhalersClubRewardHistory.styles';

interface WhalersClubRewardHistoryProps {
  records: LoyaltyRewardHistoryRecord[];
  availablePeriods: [year: number, month: number][];
  activePeriod: string;
  handleMonthChange: (month: string) => void;
  isLoading?: boolean;
}

function WhalersClubRewardHistory({
  isLoading,
  records,
  availablePeriods,
  activePeriod,
  handleMonthChange,
}: WhalersClubRewardHistoryProps) {
  const { classes } = useStyles();
  const { t } = useTranslation('loyalty');

  const dropdownOptions = availablePeriods.map((tuple) => ({
    label: `${format(new Date(tuple[0], tuple[1] - 1, 1), 'MMMM yyyy')}`,
    value: `${tuple[0]}-${tuple[1]}`,
  }));

  if (!(availablePeriods?.length > 0)) return null;

  return (
    <div className={classes.container}>
      <Title variant="headline-xl">
        {t('whalersClub.rewardHistory.title')}
      </Title>
      <div className={classes.dropdownContainer}>
        <Select
          data={dropdownOptions}
          value={activePeriod}
          onChange={handleMonthChange}
          variant="filled"
        />
      </div>

      {isLoading && <Skeleton width="100%" height={200} radius="lg" />}

      {!isLoading && records.length === 0 && (
        <Box className={classes.box} mih={200}>
          <Text variant="body-medium">{t('whalersClub.rewardHistory.empty')}</Text>
        </Box>
      )}

      {!isLoading && records.length > 0 && (
        records.map((record) => (
        <div key={record.id} className={classes.box}>
          <div className={classes.row}>
            <Text variant="subhead-medium" transform="uppercase">
              {record.periodLabel}
            </Text>
            <Text variant="subhead-medium" color={record.rewardAmount > 0 ? 'green.6' : null}>
              {match(record.rewardAmount)
                .when(n => n > 0, () => `+${CurrencyConvertor(record.rewardAmount, true)}`)
                .when(n => n === 0, () => `${CurrencyConvertor(record.rewardAmount, true)}`)
                .otherwise(() => '-')
              }
            </Text>
          </div>
          <div className={classes.content}>
            <div className={classes.row}>
              <Text variant="title-small">{t('whalersClub.rewardHistory.totalEntries')}</Text>
              <Text variant="body-medium">{CurrencyConvertor(record.metricValue, true)}</Text>
            </div>
            <div className={classes.row}>
              <Text variant="title-small">{t('whalersClub.rewardHistory.duration')}</Text>
              <Text variant="body-medium">
                {`${format(new Date(record.periodStartAt), 'MMM d, yyyy')} - ${format(new Date(record.periodEndAt), 'MMM d, yyyy')}`}
              </Text>
            </div>
            <div className={classes.row}>
              <Text variant="title-small">{t('whalersClub.rewardHistory.status')}</Text>
              <Text variant="body-medium">{record.status}</Text>
            </div>
          </div>
          <div className={classes.footer}>
            <Text variant="body-medium">
              {t('whalersClub.rewardHistory.rewardId', { rewardId: record.id })}
            </Text>
          </div>
        </div>
      )))}
      <div className={classes.footnote}>
        <Text variant="body-medium">
          {t('whalersClub.rewardHistory.note')}
        </Text>
      </div>
    </div>
  );
}

export default WhalersClubRewardHistory;
